import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useParams, Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { LayoutDashboard, Compass, ShieldCheck, FileText, FolderOpen, AlertTriangle, ClipboardCheck, Siren, ListTodo, Users, PackageOpen, Sun, Moon, LogOut, Briefcase, Eye, GraduationCap, HardHat, Mail, BookOpen, Building2, Globe, LifeBuoy, ShieldCheck as AdminIcon } from "lucide-react";
import { PlanBadge } from "@/components/common/PlanBadge";
import { GeniePanel } from "@/components/common/GeniePanel";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { FrameworkBadge } from "@/components/common/bits";

const NAV = [
  ["dashboard", "Dashboard", LayoutDashboard], ["scoping", "Scoping & SoA", Compass], ["controls", "Controls", ShieldCheck], ["policies", "Policies", FileText],
  ["evidence", "Evidence", FolderOpen], ["risks", "Risk Register", AlertTriangle], ["audits", "Audits & CAPA", ClipboardCheck], ["incidents", "Incidents", Siren],
  ["tasks", "Tasks", ListTodo], ["training", "Training", BookOpen], ["vendors", "Vendors", Building2], ["team", "Team & Storage", Users], ["trust", "Trust Centre", Globe], ["audit-pack", "Audit Pack", PackageOpen], ["notifications", "Notifications", Mail], ["support", "Support", LifeBuoy],
];
const EDU_SECTORS = ["college", "school", "university", "education"];
const MFG_SECTORS = ["manufacturing", "factory"];
const packNav = (org) => {
  const fw = org.frameworks || [];
  const items = [];
  if (EDU_SECTORS.includes(org.sector) || fw.includes("naac")) items.push(["accreditation", "NAAC Accreditation", GraduationCap]);
  if (MFG_SECTORS.includes(org.sector) || fw.includes("iso14001") || fw.includes("iso45001")) items.push(["registers", "EHS Registers", HardHat]);
  return items;
};

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const dark = theme === "dark";
  return (
    <Button variant="ghost" size="icon" onClick={() => setTheme(dark ? "light" : "dark")} data-testid="theme-toggle" aria-label="Toggle theme">
      {dark ? <Sun size={16} /> : <Moon size={16} />}
    </Button>
  );
}

export default function AppShell() {
  const { orgId } = useParams();
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);

  const load = async () => {
    const [o, m] = await Promise.all([api.get(`/orgs/${orgId}`), api.get(`/orgs/${orgId}/members`)]);
    setOrg(o.data); setMembers(m.data);
  };
  useEffect(() => { load().catch(() => navigate("/app")); /* eslint-disable-next-line */ }, [orgId]);

  if (!org) return <div className="p-10 text-muted-foreground">Loading workspace…</div>;
  const role = org.role;
  const canWrite = ["admin", "owner", "contributor", "consultant"].includes(role);
  const canManage = ["admin", "owner", "consultant"].includes(role);
  const isConsultant = session?.user?.is_consultant || session?.user?.is_platform_admin;

  return (
    <div className="min-h-screen flex">
      <aside className="w-64 shrink-0 hidden md:flex flex-col border-r bg-[hsl(var(--sidebar))] fixed inset-y-0 left-0 z-30" data-testid="sidebar">
        <div className="h-16 flex items-center px-5 border-b gap-2">
          <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-display font-extrabold">C</div>
          <span className="font-display font-bold text-lg tracking-tight">ComplyKosh</span>
        </div>
        <div className="px-4 py-4 border-b">
          <div className="eyebrow mb-2">Workspace</div>
          <Select value={orgId} onValueChange={(v) => navigate(`/org/${v}/dashboard`)}>
            <SelectTrigger data-testid="org-switcher" className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {session.memberships.map((m) => <SelectItem key={m.org_id} value={m.org_id} data-testid={`org-option-${m.org_id}`}>{m.org_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-1 mt-2">{(org.frameworks || []).map((f) => <FrameworkBadge key={f} code={f} />)}</div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {[...NAV.slice(0, 7), ...packNav(org), ...NAV.slice(7)].map(([path, label, Icon]) => (
            <NavLink key={path} to={`/org/${orgId}/${path}`} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid={`nav-${path}`}>
              <Icon size={16} /> {label}
            </NavLink>
          ))}
          {isConsultant && (
            <NavLink to="/consultant" className="nav-link mt-3 border-t pt-3" data-testid="nav-consultant"><Briefcase size={16} /> Consultant workspace</NavLink>
          )}
        </nav>
        <div className="p-4 border-t text-xs">
          <div className="font-medium truncate" data-testid="current-user-name">{session.user.name}</div>
          <div className="text-muted-foreground truncate">{session.user.email}</div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-primary" data-testid="current-role">{role}</div>
        </div>
      </aside>
      <div className="flex-1 md:ml-64 min-w-0">
        <header className="h-16 sticky top-0 z-20 flex items-center justify-between px-4 sm:px-6 lg:px-8 border-b backdrop-blur-md bg-white/90 dark:bg-slate-900/90" data-testid="topbar">
          <div className="min-w-0">
            <div className="eyebrow">{org.sector?.replace("_", " ")}</div>
            <div className="font-display font-semibold truncate" data-testid="org-name">{org.name}</div>
          </div>
          <div className="flex items-center gap-2">
            {role === "auditor" && <span className="hidden sm:flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md bg-sky-500/15 text-sky-700 dark:text-sky-300 border border-sky-500/30" data-testid="auditor-banner"><Eye size={13} /> Auditor · read-only</span>}
            <PlanBadge />
            {session.user.is_platform_admin && <Button variant="ghost" size="sm" asChild data-testid="admin-link"><Link to="/admin" className="text-xs"><AdminIcon size={13} className="mr-1.5" /> Admin</Link></Button>}
            <ThemeToggle />
            <Button variant="ghost" size="icon" onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn" aria-label="Log out"><LogOut size={16} /></Button>
          </div>
        </header>
        <main className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1400px]">
          <Outlet context={{ org, role, members, canWrite, canManage, reloadOrg: load, orgId }} />
          <GeniePanel orgId={orgId} canManage={canManage} />
        </main>
      </div>
    </div>
  );
}
